import { useEffect, useState } from "react";
import { Loader2, Truck, Plus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Drawer } from "@/components/layout/Drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Field, DrawerSection } from "@/components/forms/fields";
import { inboundService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";

const EMPTY = { store_id: "", supplier: "", supplier_order_id: "" };
const EMPTY_PRODUCT = { sku: "", product_name: "", expected_qty: 1, confirmed: false };

export function InboundDrawer({ open, onOpenChange, record, stores = [], onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [products, setProducts] = useState([{ ...EMPTY_PRODUCT }]);
  const [tracking, setTracking] = useState([""]);
  const [saving, setSaving] = useState(false);
  const isEdit = !!record;

  useEffect(() => {
    if (open) {
      if (record) {
        setForm({
          store_id: record.store_id || "",
          supplier: record.supplier || "",
          supplier_order_id: record.supplier_order_id || "",
        });
        setProducts(record.products?.length
          ? record.products.map((p) => ({ ...p }))
          : [{ ...EMPTY_PRODUCT }]);
        setTracking(record.tracking_numbers?.length ? [...record.tracking_numbers] : [""]);
      } else {
        setForm(EMPTY);
        setProducts([{ ...EMPTY_PRODUCT }]);
        setTracking([""]);
      }
    }
  }, [open, record]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const onStoreChange = (id) => {
    const store = stores.find((s) => s.id === id);
    setForm((f) => ({ ...f, store_id: id, supplier: f.supplier || store?.default_supplier || "" }));
  };

  const setProduct = (i, key, value) =>
    setProducts((ps) => ps.map((p, idx) => (idx === i ? { ...p, [key]: value } : p)));
  const addProduct = () => setProducts((ps) => [...ps, { ...EMPTY_PRODUCT }]);
  const removeProduct = (i) => setProducts((ps) => (ps.length > 1 ? ps.filter((_, idx) => idx !== i) : ps));

  const setTrack = (i, value) => setTracking((t) => t.map((v, idx) => (idx === i ? value : v)));
  const addTrack = () => setTracking((t) => [...t, ""]);
  const removeTrack = (i) => setTracking((t) => (t.length > 1 ? t.filter((_, idx) => idx !== i) : t));

  const validProducts = products.filter((p) => p.sku.trim() && Number(p.expected_qty) > 0);
  const storeError = !form.store_id ? "Store is required" : "";
  const productsError = validProducts.length === 0 ? "Add at least one product with a SKU and expected quantity" : "";
  const canSave = !storeError && !productsError && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body = {
        store_id: form.store_id,
        supplier: form.supplier,
        supplier_order_id: form.supplier_order_id,
        tracking_numbers: tracking.map((t) => t.trim()).filter(Boolean),
        products: validProducts.map((p) => ({
          sku: p.sku.trim(),
          product_name: (p.product_name || "").trim(),
          expected_qty: Number(p.expected_qty),
          received_qty: p.received_qty || 0,
          confirmed: !!p.confirmed,
        })),
      };
      if (isEdit) { await inboundService.update(record.id, body); toast.success("Shipment updated"); }
      else { await inboundService.create(body); toast.success("Shipment created"); }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      icon={Truck}
      title={isEdit ? `Edit ${record?.inbound_id || "Shipment"}` : "New Inbound Shipment"}
      description={isEdit ? "Update shipment details. Confirmed lines are locked." : "Log a supplier shipment to expect."}
      widthClass="sm:max-w-xl"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="inbound-drawer-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid="inbound-drawer-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Create shipment"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <DrawerSection title="Shipment details">
          <Field label="Store" required error={storeError}>
            <Select value={form.store_id} onValueChange={onStoreChange} disabled={isEdit}>
              <SelectTrigger data-testid="inbound-store-select"><SelectValue placeholder="Select a store" /></SelectTrigger>
              <SelectContent>
                {stores.map((s) => (<SelectItem key={s.id} value={s.id}>{s.store_name}</SelectItem>))}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Supplier"><Input value={form.supplier} onChange={(e) => set("supplier")(e.target.value)} placeholder="Acme Supply Co." data-testid="inbound-supplier-input" /></Field>
            <Field label="Supplier Order ID"><Input value={form.supplier_order_id} onChange={(e) => set("supplier_order_id")(e.target.value)} data-testid="inbound-supplier-order-input" /></Field>
          </div>
        </DrawerSection>

        <DrawerSection title="Tracking numbers">
          <div className="space-y-2">
            {tracking.map((t, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`inbound-tracking-${i}`}>
                <Input value={t} onChange={(e) => setTrack(i, e.target.value)} placeholder="1Z999AA10123456784" data-testid={`inbound-tracking-input-${i}`} />
                <button type="button" onClick={() => removeTrack(i)} disabled={tracking.length === 1}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-error-light hover:text-error disabled:opacity-40"
                  data-testid={`inbound-tracking-remove-${i}`} aria-label="Remove tracking">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <Button variant="ghost" size="sm" className="mt-1 gap-2 text-muted-foreground hover:text-foreground" onClick={addTrack} data-testid="inbound-add-tracking">
            <Plus className="h-3.5 w-3.5" /> Add tracking number
          </Button>
        </DrawerSection>

        <DrawerSection title="Products">
          <div className="space-y-3">
            {products.map((p, i) => (
              <div key={i} className="grid grid-cols-12 items-end gap-2" data-testid={`inbound-product-${i}`}>
                <div className="col-span-4">
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">SKU</label>
                  <Input value={p.sku} onChange={(e) => setProduct(i, "sku", e.target.value)} disabled={p.confirmed} placeholder="FF-100" data-testid={`inbound-product-sku-${i}`} />
                </div>
                <div className="col-span-5">
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Product Name</label>
                  <Input value={p.product_name} onChange={(e) => setProduct(i, "product_name", e.target.value)} disabled={p.confirmed} placeholder="Widget" data-testid={`inbound-product-name-${i}`} />
                </div>
                <div className="col-span-2">
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Expected Qty</label>
                  <Input type="number" min={1} value={p.expected_qty} onChange={(e) => setProduct(i, "expected_qty", e.target.value)} disabled={p.confirmed} data-testid={`inbound-product-qty-${i}`} />
                </div>
                <div className="col-span-1">
                  {p.confirmed ? (
                    <div className="flex h-10 w-full items-center justify-center text-success" title="Confirmed / locked"><Lock className="h-4 w-4" /></div>
                  ) : (
                    <button type="button" onClick={() => removeProduct(i)} disabled={products.length === 1}
                      className="flex h-10 w-full items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-error-light hover:text-error disabled:opacity-40"
                      data-testid={`inbound-product-remove-${i}`} aria-label="Remove product">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {productsError && <p className="text-xs font-medium text-error-dark">{productsError}</p>}
          <Button variant="outline" size="sm" className="mt-1 gap-2" onClick={addProduct} data-testid="inbound-add-product">
            <Plus className="h-4 w-4" /> Add product
          </Button>
        </DrawerSection>
      </div>
    </Drawer>
  );
}
